import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CardProduct from '@/domains/mobile-web/components/CardProduct/CardProduct';
import CardProductBundling from '@/domains/mobile-web/components/CardProductBundling/CardProductBundling';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useAddCartItem, useCartCount } from '@/domains/mobile-web/hooks/useCart';
import { useProductPages } from '@/domains/mobile-web/hooks/useProducts';
import type { ProductSummary } from '@/domains/mobile-web/types/product.types';
import { flyToCart } from '@/domains/mobile-web/utils/flyToCart';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import Seo from '@/shared/components/ui/Seo/Seo';
import { SkeletonCardProduct, SkeletonCardProductBundling } from '@/shared/components/ui/Skeleton';
import useRequireLogin from '@/shared/hooks/useRequireLogin';
import { canonicalUrl } from '@/shared/utils/site';

const SKELETON_PRODUCT_COUNT = 4;
const BOOK_PATH = '/store/book';

// schema.org ItemList of the loaded products, so search engines can show the
// covers, titles, authors and prices even though the detail page needs a login
const productListJsonLd = (products: ProductSummary[]) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Daftar Buku Rumah Kebijaksanaan',
  url: canonicalUrl(BOOK_PATH),
  numberOfItems: products.length,
  itemListElement: products.map((product, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'Product',
      name: product.title,
      ...(product.image_url ? { image: product.image_url } : {}),
      ...(product.author ? { author: { '@type': 'Person', name: product.author } } : {}),
      offers: {
        '@type': 'Offer',
        price: Number(product.final_price),
        priceCurrency: product.currency || 'IDR',
        availability: product.is_available
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        url: canonicalUrl(BOOK_PATH),
      },
    },
  })),
});
// One request per typed word rather than per keystroke
const SEARCH_DEBOUNCE_MS = 300;

const Book = () => {
  const navigate = useNavigate();
  const requireLogin = useRequireLogin();

  const [keyword, setKeyword] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(keyword.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [keyword]);

  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useProductPages(search);
  const rows = data?.pages.flatMap((page) => page.rows) ?? [];
  const bundles = rows.filter((row) => row.product_type === 'bundle');
  const books = rows.filter((row) => row.product_type === 'book');
  const openProduct = (product: ProductSummary) =>
    navigate(`/store/product-detail/${product.slug}`);
  const addCartItem = useAddCartItem();
  const addToCart = (product: ProductSummary, origin: HTMLElement) =>
    requireLogin(() => {
      flyToCart({ imageUrl: product.image_url, from: origin });
      addCartItem.mutate({ productId: product.id });
    });
  const buyNow = (product: ProductSummary) =>
    requireLogin(() => navigate(`/store/checkout?product=${encodeURIComponent(product.slug)}`));
  const cartCount = useCartCount();

  // Loads the next page when the end of the list scrolls into view
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <>
      <Seo
        title="Daftar Buku"
        description="Jelajahi dan beli buku pilihan di Rumah Kebijaksanaan: buku satuan dan paket bundling dengan harga terbaik."
        path={BOOK_PATH}
        jsonLd={rows.length > 0 && !search ? productListJsonLd(rows) : undefined}
      />
      <HeaderMobile>
        <TopBarHeader
          type="textBucket"
          totalProduct={cartCount}
          title="Buku"
          onAction={() => navigate('/store/cart')}
        />
      </HeaderMobile>

      <WrapperMobile>
        <h1 className="sr-only">Daftar Buku Rumah Kebijaksanaan</h1>
        <input
          type="text"
          className="input-db-text"
          placeholder="Cari judul buku, penulis...."
          aria-label="Cari judul buku atau penulis"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
        />

        {isLoading ? (
          <>
            <SkeletonCardProductBundling />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 md:grid-cols-3  gap-6 ">
              {Array.from({ length: SKELETON_PRODUCT_COUNT }, (_, i) => (
                <SkeletonCardProduct key={i} />
              ))}
            </div>
          </>
        ) : isError ? (
          <p className="body1 text-text-grey">Gagal memuat buku.</p>
        ) : (
          <>
            {bundles.map((item) => (
              <CardProductBundling
                key={item.id}
                product={item}
                onOpen={openProduct}
                onBuy={buyNow}
                onAddCart={addToCart}
              />
            ))}

            {books.length === 0 && !hasNextPage && search && (
              <p className="body1 text-text-grey">Buku tidak ditemukan.</p>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 md:grid-cols-3  gap-6 ">
              {books.map((book) => (
                <CardProduct
                  key={book.id}
                  product={book}
                  onOpen={openProduct}
                  onBuy={buyNow}
                  onAddCart={addToCart}
                />
              ))}
            </div>

            {hasNextPage && <div ref={sentinelRef} aria-hidden="true" />}
          </>
        )}
      </WrapperMobile>
    </>
  );
};

export default Book;
